/**
 * DL-01 — the Data Acquisitions table's "Download images" cell (the DA
 * table — story §B): an icon button on every row the caller may download
 * (`image-download/eligibility.ts`), nothing on the others.
 *
 *   own rows       role ∈ {tenant_admin, project_manager, expert_analyst}
 *   customer rows  `ownership: member` + `role: project_customer` AND the
 *                  owner's `customer_image_download` flag, read off the
 *                  project (one query per project, shared by its rows).
 *
 * Today the BE lists no DA for a customer grant (XT-D22 — the customer sees
 * the outcome surface only); the customer branch is in place for the
 * XT-D38 supersession the story proposes, and renders nothing until the
 * rows arrive.
 */
import type { ReactElement } from "react";
import type { DataAcquisition } from "@aeriosiq/types";
import {
  DownloadImagesButton,
  canDownloadImages,
  needsCustomerFlag,
  useCustomerImageDownloadFlag,
} from "../image-download/index.js";

export interface DownloadImagesCellProps {
  row: DataAcquisition;
  /** Roles from the /me payload (undefined while unknown → nothing). */
  myRoles: readonly string[] | undefined;
}

export function DownloadImagesCell({
  row,
  myRoles,
}: DownloadImagesCellProps): ReactElement | null {
  const tell = {
    ownership: row.ownership,
    accessRole: row.role ?? null,
    scopeKind: "da" as const,
  };
  const flag = useCustomerImageDownloadFlag(
    row.projectId,
    needsCustomerFlag(tell),
  );
  const eligible = canDownloadImages({
    ...tell,
    roles: myRoles,
    customerDownloadEnabled: flag,
  });
  return (
    <DownloadImagesButton
      scope={{
        kind: "da",
        id: row.id,
        label: `Data set ${String(row.instructionCode)}${
          row.assetIdentifier ? ` · ${row.assetIdentifier}` : ""
        }`,
      }}
      eligible={eligible}
      variant="icon"
    />
  );
}
